'use client';

import React, { useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Header from '@/components/Header';
import Link from 'next/link';

// ─── Types ────────────────────────────────────────────────────────────────────
interface PropertyData {
  id: string;
  name: string;
  description: string;
  type: string;
  location: string;
  city: string;
  country: string;
  bedrooms: number;
  bathrooms: number;
  guests: number;
  status: 'active' | 'inactive' | 'pending';
  pricePerNight: number;
  cleaningFee: number;
  minNights: number;
  instantBook: boolean;
  amenities: string[];
  photos: { id: string; url: string; alt: string; isPrimary: boolean }[];
}

// ─── Mock Data ────────────────────────────────────────────────────────────────
const MOCK_PROPERTIES: Record<string, PropertyData> = {
  '1': {
    id: '1',
    name: 'Lakeside Villa Entebbe',
    description: 'A stunning lakeside villa perched on the shores of Lake Victoria. Enjoy breathtaking sunsets, a private pool, and lush tropical gardens. Perfect for families and groups seeking a luxurious retreat in Entebbe.',
    type: 'Villa',
    location: 'Entebbe, Uganda',
    city: 'Entebbe',
    country: 'Uganda',
    bedrooms: 4,
    bathrooms: 3,
    guests: 8,
    status: 'active',
    pricePerNight: 180000,
    cleaningFee: 30000,
    minNights: 2,
    instantBook: true,
    amenities: ['WiFi', 'Pool', 'Kitchen', 'Parking', 'Air conditioning', 'BBQ grill', 'Garden', 'TV'],
    photos: [
      { id: 'p1', url: 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=800&q=80', alt: 'Luxury lakeside villa with pool overlooking Lake Victoria', isPrimary: true },
      { id: 'p2', url: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&q=80', alt: 'Spacious living room with panoramic lake views', isPrimary: false },
      { id: 'p3', url: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80', alt: 'Master bedroom with king bed and en-suite bathroom', isPrimary: false },
      { id: 'p4', url: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&q=80', alt: 'Modern kitchen fully equipped for self-catering', isPrimary: false },
    ],
  },
  '2': {
    id: '2',
    name: 'Kampala City Apartment',
    description: 'A sleek, modern apartment in the heart of Kololo with panoramic views of Kampala. Walking distance to top restaurants, embassies, and business hubs. Ideal for business travelers and urban explorers.',
    type: 'Apartment',
    location: 'Kololo, Kampala',
    city: 'Kampala',
    country: 'Uganda',
    bedrooms: 2,
    bathrooms: 2,
    guests: 4,
    status: 'active',
    pricePerNight: 95000,
    cleaningFee: 20000,
    minNights: 1,
    instantBook: false,
    amenities: ['WiFi', 'Air conditioning', 'Kitchen', 'TV', 'Washer', 'Security cameras'],
    photos: [
      { id: 'p1', url: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80', alt: 'Modern city apartment with panoramic views of Kampala skyline', isPrimary: true },
      { id: 'p2', url: 'https://images.unsplash.com/photo-1560185007-cde436f6a4d0?w=800&q=80', alt: 'Open-plan living and dining area with city views', isPrimary: false },
    ],
  },
  '3': {
    id: '3',
    name: 'Bwindi Forest Lodge',
    description: 'An immersive eco-lodge nestled at the edge of Bwindi Impenetrable Forest. Wake up to birdsong, spot gorillas nearby, and unwind by the fireplace. A once-in-a-lifetime experience for nature lovers.',
    type: 'Lodge',
    location: 'Bwindi, Uganda',
    city: 'Bwindi',
    country: 'Uganda',
    bedrooms: 3,
    bathrooms: 2,
    guests: 6,
    status: 'active',
    pricePerNight: 250000,
    cleaningFee: 40000,
    minNights: 3,
    instantBook: false,
    amenities: ['WiFi', 'Kitchen', 'Garden', 'First aid kit', 'Fire extinguisher', 'BBQ grill'],
    photos: [
      { id: 'p1', url: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&q=80', alt: 'Rustic forest lodge surrounded by lush Bwindi Impenetrable Forest', isPrimary: true },
      { id: 'p2', url: 'https://images.unsplash.com/photo-1587061949409-02df41d5e562?w=800&q=80', alt: 'Cozy bedroom with forest views and natural wood decor', isPrimary: false },
    ],
  },
  '4': {
    id: '4',
    name: 'Jinja Nile View Cottage',
    description: 'A charming riverside cottage with sweeping views of the Nile. Perfect for adventure seekers — white-water rafting, kayaking, and bungee jumping are minutes away. Relax on the deck as the river flows by.',
    type: 'Cottage',
    location: 'Jinja, Uganda',
    city: 'Jinja',
    country: 'Uganda',
    bedrooms: 2,
    bathrooms: 1,
    guests: 4,
    status: 'inactive',
    pricePerNight: 120000,
    cleaningFee: 25000,
    minNights: 2,
    instantBook: false,
    amenities: ['WiFi', 'Kitchen', 'Parking', 'Garden', 'BBQ grill'],
    photos: [
      { id: 'p1', url: 'https://images.unsplash.com/photo-1510798831971-661eb04b3739?w=800&q=80', alt: 'Charming cottage with scenic views of the Nile River in Jinja', isPrimary: true },
    ],
  },
};

const ALL_AMENITIES = [
  'WiFi', 'Air conditioning', 'Kitchen', 'Parking', 'Pool', 'Gym',
  'TV', 'Washer', 'Dryer', 'Hot tub', 'BBQ grill', 'Garden',
  'Security cameras', 'Smoke alarm', 'First aid kit', 'Fire extinguisher',
];

const TABS = [
  { id: 'details', label: 'Details', icon: '📋' },
  { id: 'amenities', label: 'Amenities', icon: '✨' },
  { id: 'pricing', label: 'Pricing', icon: '💰' },
  { id: 'photos', label: 'Photos', icon: '📸' },
];

const statusColors: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-gray-100 text-gray-600',
  pending: 'bg-amber-100 text-amber-700',
};

// ─── Details Tab ──────────────────────────────────────────────────────────────
function DetailsTab({ data, onChange }: { data: PropertyData; onChange: (d: Partial<PropertyData>) => void }) {
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Property Name</label>
        <input
          type="text"
          value={data.name}
          onChange={(e) => onChange({ name: e.target.value })}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition"
          placeholder="e.g. Lakeside Villa Entebbe"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description</label>
        <textarea
          value={data.description}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={5}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition resize-none"
          placeholder="Describe your property to guests..."
        />
        <p className="text-xs text-gray-400 mt-1">{data.description.length} / 1000 characters</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Property Type</label>
          <select
            value={data.type}
            onChange={(e) => onChange({ type: e.target.value })}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition bg-white"
          >
            {['Villa', 'Apartment', 'Lodge', 'Cottage', 'Hotel Room', 'Private Room', 'Entire Home'].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Status</label>
          <select
            value={data.status}
            onChange={(e) => onChange({ status: e.target.value as PropertyData['status'] })}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition bg-white"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending Review</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">City</label>
          <input
            type="text"
            value={data.city}
            onChange={(e) => onChange({ city: e.target.value })}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Country</label>
          <input
            type="text"
            value={data.country}
            onChange={(e) => onChange({ country: e.target.value })}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition"
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        {[
          { key: 'bedrooms', label: 'Bedrooms', icon: '🛏' },
          { key: 'bathrooms', label: 'Bathrooms', icon: '🚿' },
          { key: 'guests', label: 'Max Guests', icon: '👥' },
        ].map(({ key, label, icon }) => (
          <div key={key}>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">{icon} {label}</label>
            <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => onChange({ [key]: Math.max(1, (data[key as keyof PropertyData] as number) - 1) })}
                className="px-3 py-3 text-gray-500 hover:bg-gray-50 font-bold text-lg transition"
              >−</button>
              <span className="flex-1 text-center text-sm font-bold text-gray-900">
                {data[key as keyof PropertyData] as number}
              </span>
              <button
                onClick={() => onChange({ [key]: (data[key as keyof PropertyData] as number) + 1 })}
                className="px-3 py-3 text-gray-500 hover:bg-gray-50 font-bold text-lg transition"
              >+</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Amenities Tab ────────────────────────────────────────────────────────────
function AmenitiesTab({ data, onChange }: { data: PropertyData; onChange: (d: Partial<PropertyData>) => void }) {
  const toggle = (amenity: string) => {
    const current = data.amenities;
    const updated = current.includes(amenity)
      ? current.filter((a) => a !== amenity)
      : [...current, amenity];
    onChange({ amenities: updated });
  };

  return (
    <div>
      <p className="text-sm text-gray-500 mb-5">Select all amenities available at your property. Accurate amenities help guests find your listing.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {ALL_AMENITIES.map((amenity) => {
          const selected = data.amenities.includes(amenity);
          return (
            <button
              key={amenity}
              onClick={() => toggle(amenity)}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-semibold transition-all ${
                selected
                  ? 'bg-rose-50 border-rose-400 text-rose-700' :'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              <span className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${selected ? 'bg-rose-500' : 'bg-gray-200'}`}>
                {selected && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </span>
              {amenity}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-gray-400 mt-4">{data.amenities.length} amenities selected</p>
    </div>
  );
}

// ─── Pricing Tab ──────────────────────────────────────────────────────────────
function PricingTab({ data, onChange }: { data: PropertyData; onChange: (d: Partial<PropertyData>) => void }) {
  const estimatedMonthly = data.pricePerNight * 22 * 0.75;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nightly Rate (UGX)</label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">UGX</span>
            <input
              type="number"
              value={data.pricePerNight}
              onChange={(e) => onChange({ pricePerNight: Number(e.target.value) })}
              className="w-full border border-gray-200 rounded-xl pl-14 pr-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Cleaning Fee (UGX)</label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">UGX</span>
            <input
              type="number"
              value={data.cleaningFee}
              onChange={(e) => onChange({ cleaningFee: Number(e.target.value) })}
              className="w-full border border-gray-200 rounded-xl pl-14 pr-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Minimum Nights</label>
          <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden">
            <button
              onClick={() => onChange({ minNights: Math.max(1, data.minNights - 1) })}
              className="px-4 py-3 text-gray-500 hover:bg-gray-50 font-bold text-lg transition"
            >−</button>
            <span className="flex-1 text-center text-sm font-bold text-gray-900">{data.minNights} night{data.minNights !== 1 ? 's' : ''}</span>
            <button
              onClick={() => onChange({ minNights: data.minNights + 1 })}
              className="px-4 py-3 text-gray-500 hover:bg-gray-50 font-bold text-lg transition"
            >+</button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Instant Book</label>
          <button
            onClick={() => onChange({ instantBook: !data.instantBook })}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm font-semibold transition-all ${
              data.instantBook ? 'bg-rose-50 border-rose-400 text-rose-700' : 'bg-white border-gray-200 text-gray-600'
            }`}
          >
            <span>{data.instantBook ? '⚡ Instant Book On' : '⚡ Instant Book Off'}</span>
            <div className={`w-10 h-5 rounded-full transition-all relative ${data.instantBook ? 'bg-rose-500' : 'bg-gray-200'}`}>
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${data.instantBook ? 'left-5' : 'left-0.5'}`} />
            </div>
          </button>
        </div>
      </div>

      {/* Earnings estimate */}
      <div className="bg-gradient-to-br from-rose-50 to-orange-50 rounded-2xl p-5 border border-rose-100">
        <h4 className="text-sm font-bold text-gray-800 mb-3">📊 Estimated Earnings</h4>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-extrabold text-gray-900">UGX {data.pricePerNight.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-0.5">per night</div>
          </div>
          <div>
            <div className="text-lg font-extrabold text-rose-600">UGX {Math.round(estimatedMonthly).toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-0.5">est. monthly</div>
          </div>
          <div>
            <div className="text-lg font-extrabold text-gray-900">UGX {Math.round(estimatedMonthly * 12).toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-0.5">est. yearly</div>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3">Based on 75% occupancy rate. Actual earnings may vary.</p>
      </div>
    </div>
  );
}

// ─── Photos Tab ───────────────────────────────────────────────────────────────
function PhotosTab({ data, onChange }: { data: PropertyData; onChange: (d: Partial<PropertyData>) => void }) {
  const setPrimary = (id: string) => {
    onChange({ photos: data.photos.map((p) => ({ ...p, isPrimary: p.id === id })) });
  };

  const removePhoto = (id: string) => {
    const updated = data.photos.filter((p) => p.id !== id);
    if (updated.length > 0 && !updated.some((p) => p.isPrimary)) {
      updated[0].isPrimary = true;
    }
    onChange({ photos: updated });
  };

  return (
    <div>
      <p className="text-sm text-gray-500 mb-5">Manage your property photos. The primary photo is shown as the cover image in search results.</p>

      {/* Upload area */}
      <div className="border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center mb-6 hover:border-rose-300 transition-colors cursor-pointer group">
        <div className="text-4xl mb-3">📸</div>
        <p className="text-sm font-semibold text-gray-700 group-hover:text-rose-600 transition-colors">Click to upload photos</p>
        <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 10MB each · Minimum 5 photos recommended</p>
      </div>

      {/* Photo grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {data.photos.map((photo) => (
          <div key={photo.id} className="relative group rounded-xl overflow-hidden border border-gray-100 shadow-sm aspect-video">
            <img src={photo.url} alt={photo.alt} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all" />

            {/* Primary badge */}
            {photo.isPrimary && (
              <span className="absolute top-2 left-2 bg-rose-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                Cover
              </span>
            )}

            {/* Actions */}
            <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-all">
              {!photo.isPrimary && (
                <button
                  onClick={() => setPrimary(photo.id)}
                  className="bg-white text-gray-800 text-xs font-bold px-3 py-1.5 rounded-lg shadow hover:bg-rose-500 hover:text-white transition-all"
                >
                  Set Cover
                </button>
              )}
              <button
                onClick={() => removePhoto(photo.id)}
                className="bg-white text-red-500 text-xs font-bold px-3 py-1.5 rounded-lg shadow hover:bg-red-500 hover:text-white transition-all"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      {data.photos.length === 0 && (
        <div className="text-center py-8 text-gray-400 text-sm">No photos yet. Upload some to attract guests.</div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function PropertyEditPage() {
  const params = useParams();
  const router = useRouter();
  const id = params?.id as string;

  const initial = MOCK_PROPERTIES[id] ?? MOCK_PROPERTIES['1'];
  const [property, setProperty] = useState<PropertyData>(initial);
  const [activeTab, setActiveTab] = useState('details');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = (updates: Partial<PropertyData>) => {
    setProperty((prev) => ({ ...prev, ...updates }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    await new Promise((r) => setTimeout(r, 900));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
            <Link href="/host-dashboard" className="hover:text-rose-500 transition-colors font-medium">Host Dashboard</Link>
            <span>›</span>
            <span className="text-gray-400">Properties</span>
            <span>›</span>
            <span className="text-gray-900 font-semibold truncate max-w-[200px]">{property.name}</span>
          </div>

          {/* Page header */}
          <div className="flex items-start justify-between mb-6 gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 border border-gray-100 shadow-sm">
                <img
                  src={property.photos[0]?.url ?? 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=100&q=80'}
                  alt={property.photos[0]?.alt ?? property.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-extrabold text-gray-900 truncate" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                  {property.name}
                </h1>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-gray-500">{property.location}</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusColors[property.status]}`}>
                    {property.status}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link
                href={`/property/${property.id}`}
                className="text-sm font-semibold text-gray-600 hover:text-rose-500 px-4 py-2 rounded-xl border border-gray-200 hover:border-rose-300 transition-all bg-white"
              >
                View Listing ↗
              </Link>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`text-sm font-bold px-5 py-2 rounded-xl transition-all ${
                  saved
                    ? 'bg-emerald-500 text-white'
                    : saving
                    ? 'bg-rose-300 text-white cursor-not-allowed' :'bg-rose-500 hover:bg-rose-600 text-white'
                }`}
              >
                {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Changes'}
              </button>
            </div>
          </div>

          {/* Tab nav */}
          <div className="flex gap-1 bg-white rounded-2xl p-1.5 shadow-sm border border-gray-100 mb-6 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all flex-1 justify-center ${
                  activeTab === tab.id
                    ? 'bg-rose-500 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            {activeTab === 'details' && <DetailsTab data={property} onChange={handleChange} />}
            {activeTab === 'amenities' && <AmenitiesTab data={property} onChange={handleChange} />}
            {activeTab === 'pricing' && <PricingTab data={property} onChange={handleChange} />}
            {activeTab === 'photos' && <PhotosTab data={property} onChange={handleChange} />}
          </div>

          {/* Bottom save bar */}
          <div className="mt-4 flex items-center justify-between bg-white rounded-2xl border border-gray-100 shadow-sm px-6 py-4">
            <p className="text-xs text-gray-400">Changes are saved to your listing immediately after clicking Save.</p>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`text-sm font-bold px-6 py-2.5 rounded-xl transition-all ${
                saved
                  ? 'bg-emerald-500 text-white'
                  : saving
                  ? 'bg-rose-300 text-white cursor-not-allowed' :'bg-rose-500 hover:bg-rose-600 text-white'
              }`}
            >
              {saving ? 'Saving…' : saved ? '✓ Changes Saved' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
